import React, { useState, useMemo } from 'react';
import * as Yup from 'yup';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import allForm from '../../Stateless/Form';
import { COLORS } from '../../../theme/themeConstants';
import { MultiSelectField } from './MultiSelectField';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useAllMerchant } from '../../Query/MerchantQuery';

const validationSchema = Yup.object().shape({
  fromDate: Yup.string().nullable(),
  toDate: Yup.string().nullable(),
  storeType: Yup.string().nullable(),
  store: Yup.array().nullable(),
  storeOwner: Yup.array().nullable(),
  orderStatus: Yup.array().nullable(),
  paymentMethod: Yup.string().nullable(),
});

const OrderSummaryReport: React.FC = () => {
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);

  // Fetch data from SDK
  const { data: storesData } = useAllStores({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
  });

  const { data: merchantData } = useAllMerchant({ initialData: {} });

  const storeTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'Food', label: 'Food' },
    { value: 'Grocery', label: 'Grocery' },
    { value: 'Pharmacy', label: 'Pharmacy' },
  ];

  const storeOptions = useMemo(
    () =>
      (storesData?.data || []).map((store: any) => ({
        value: store.id,
        label:
          store.restaurantDetails?.storeName ||
          store.nameEnglish ||
          store.name ||
          'Unknown Store',
      })),
    [storesData]
  );

  const storeOwnerOptions = useMemo(
    () =>
      (merchantData ?? []).map((merchant: any) => ({
        value: merchant.id,
        label:
          `${merchant.firstName ?? ''} ${merchant.lastName ?? ''}`.trim() ||
          merchant.name ||
          'Unknown Owner',
      })),
    [merchantData]
  );

  const orderStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'ready', label: 'Ready' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const paymentMethodOptions = [
    { value: 'all', label: 'All' },
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'wallet_cash', label: 'Wallet + Cash' },
    { value: 'wallet_online', label: 'Wallet + Online' },
  ];

  const formConfig = {
    initialValues: {
      fromDate: '',
      toDate: '',
      storeType: '',
      store: [],
      storeOwner: [],
      orderStatus: [],
      paymentMethod: '',
    },
    validationSchema,
    onSubmit: (values: Record<string, any>) => {
      console.log('Order Summary Report form submit:', values);
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedFromDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedFromDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedToDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedToDate(newValue);
                form.setFieldValue(field.name, newValue ? newValue.toISOString() : '');
              }}
              slotProps={{
                textField: {
                  placeholder: 'MM/DD/YYYY',
                  sx: { width: '300px' },
                },
              }}
            />
          </LocalizationProvider>
        ),
      },
      {
        name: 'storeType',
        label: 'Store Type:',
        type: 'select',
        options: storeTypeOptions,
      },
      {
        name: 'store',
        label: 'Stores:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={storeOptions}
            placeholder="Select Stores"
            width="300px"
          />
        ),
      },
      {
        name: 'storeOwner',
        label: 'Store Owner:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={storeOwnerOptions}
            placeholder="Select Store Owners"
            width="300px"
          />
        ),
      },
      {
        name: 'orderStatus',
        label: 'Order Status:',
        type: 'custom',
        component: ({ field, form }: any) => (
          <MultiSelectField
            field={field}
            form={form}
            options={orderStatusOptions}
            placeholder="Select Order Status"
            width="300px"
          />
        ),
      },
      {
        name: 'paymentMethod',
        label: 'Payment Method:',
        type: 'select',
        options: paymentMethodOptions,
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: COLORS.primary.main,
            textTransform: 'none',
            '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) },
          }}
        >
          Download
        </Button>
        <Button type="reset" variant="outlined">Reset</Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: 3, color: '#333', textAlign: 'left' }}
      >
        Order Summary Report
      </Typography>
      <Box sx={{ backgroundColor: 'white', borderRadius: 2, padding: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: 900, margin: '0 auto' }}>
        {allForm(formConfig)()}
      </Box>
    </Box>
  );
};

export default OrderSummaryReport;
